import { C, radius, scrim } from "../theme.ts";
import { useCloseOnBack } from "../useCloseOnBack.ts";

/**
 * The shell the menu and the detail sheet both slide up from the bottom.
 * They never agreed on padding or on how the top border is spent — the menu
 * a plain rule, the detail sheet the task's value colour — so those stay
 * explicit per call site rather than defaulted here into a third, invented
 * value. What was already identical (the overlay, the corner radius, the
 * width cap) is what this actually shares.
 */
export function BottomSheet({
  onClose, zIndex, topBorder, padding, scroll, children,
}: {
  onClose: () => void;
  zIndex: number;
  /** e.g. `{ width: border.hairline, colour: C.rule }` for the menu, or
   * `{ width: border.accent, colour: valueAccent(t.value_colour) }` for the
   * detail sheet. */
  topBorder: { width: number; colour: string };
  padding: string;
  /** Caps the sheet's height and lets its own content scroll — both the
   * detail sheet and the menu need this; the latter grew past one screen
   * once Future/Cal/Money and Ask AI/Lock joined the tabs already in it. */
  scroll?: boolean;
  children: React.ReactNode;
}) {
  // Mounted only while this sheet is open — see the file for why that is
  // what the hook needs.
  useCloseOnBack(onClose);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: scrim, display: "flex", alignItems: "flex-end", zIndex }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.ground, width: "100%", maxWidth: 560, margin: "0 auto",
          borderRadius: radius.sheetTop,
          borderTop: `${topBorder.width}px solid ${topBorder.colour}`,
          padding, boxSizing: "border-box",
          ...(scroll
            // `border-box` so the cap includes the padding — left as
            // `content-box`, the padding this sheet already carries (the
            // menu's safe-area bottom padding especially) pushed the box's
            // real height past the viewport, and the part that landed above
            // y=0 was gone: not clipped-with-scroll, just never painted,
            // with nothing a swipe could bring back. `70vh` on top of that,
            // rather than `88vh`, leaves enough margin that this doesn't
            // recur on a shorter phone or with the on-screen keyboard's
            // sibling, the browser's own collapsing URL bar, taking real
            // pixels back out of the viewport.
            ? { maxHeight: "min(70dvh, calc(100dvh - 48px - env(safe-area-inset-top)))", overflowY: "auto" as const, overscrollBehavior: "contain" as const }
            : {}),
        }}
      >
        {children}
        <div aria-hidden style={{ height: "env(safe-area-inset-bottom)" }} />
      </div>
    </div>
  );
}
